// Histórico dos relatórios gerados na aba (0.53): o app guarda só o atalho; tocar abre no app do celular
// (Drive, OneDrive, Excel, leitor de PDF) no lugar onde o arquivo foi gravado.
import { useEffect, useState } from 'react';
import { Alert, Platform, Pressable, Text, View } from 'react-native';
import type { AbaId } from '../store/types';
import { abrirRelatorio, pedirRelatorio, tirarRelatorio, usePedidoRelatorio, useRelatorios, type Relatorio } from '../lib/relatorios';
import { semBloqueio } from '../auth/AuthContext';
import { AcoesItem } from './Acoes';
import { Ordenavel } from './Arrastar';
import { Arquivados, useSelecao } from './Selecao';
import { useOrganizar } from '../lib/organizar';
import { paraLixeira } from '../lib/lixeira';
import { C, alpha } from './theme';
import { br, hm, horaTela } from '../lib/date';
import { t } from '../lib/i18n';

const icone = (m: string) => (m === 'application/pdf' ? '📄' : m.includes('sheet') || m.includes('excel') || m === 'text/csv' ? '📊'
  : m.includes('word') ? '📝' : m.includes('zip') ? '🗂️' : m.startsWith('image/') ? '🖼️' : '📎');
const onde = (r: Relatorio) => (r.url && !r.uri ? '☁️ ' + t('na nuvem') : r.doApp ? '📱 ' + t('neste celular') : '📁 ' + t('na pasta escolhida'));

export function RelatoriosGerados({ aba, cor }: { aba: AbaId; cor: string }) {
  const todosRel = useRelatorios(aba);
  const [todos, setTodos] = useState(false);
  const org = useOrganizar(`${aba}-relatorios`);
  const sep = org.separar(todosRel, r => r.id);
  const lista = sep.ativos;
  // arquivar em grupo; excluir é um por um (o arquivo guardado no celular sai junto e não volta)
  const sel = useSelecao({ itens: lista, chave: r => r.id, cor, onArquivar: ids => org.arquivar(ids) });
  // "⏰ Me avise" deste relatório: tocar no aviso abre o próprio relatório (não só a aba)
  const pedido = usePedidoRelatorio();
  useEffect(() => {
    const r = pedido ? todosRel.find(x => x.id === pedido) : undefined;
    if (!r) return;
    pedirRelatorio(null);
    semBloqueio(() => abrirRelatorio(r)).catch(e => Alert.alert(t('Não foi possível abrir'), String(e?.message ?? e)));
  }, [pedido, todosRel]); // eslint-disable-line react-hooks/exhaustive-deps
  if (Platform.OS === 'web' || !todosRel.length) return null;
  const mostrar = todos || sel.ativa ? lista : lista.slice(0, 3);
  // só o atalho (Drive, OneDrive, pasta escolhida): vai para a lixeira; arquivo da pasta do app: sai de vez, com confirmação
  const excluir = (r: Relatorio) => {
    if (!r.doApp) { paraLixeira(aba, 'relatorio', r.nome, r); tirarRelatorio(r.id); return; }
    Alert.alert(t('Excluir {nome}?', { nome: r.nome }), t('O arquivo sai deste celular. As cópias que você salvou no Drive, OneDrive ou mandou para alguém continuam lá.'), [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => tirarRelatorio(r.id) }]);
  };
  const confirmar = (r: Relatorio) => (r.doApp ? excluir(r) : Alert.alert(t('Excluir {nome}?', { nome: r.nome }), t('Fica 30 dias na lixeira.'), [
    { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluir(r) }]));
  const abrir = (r: Relatorio) => semBloqueio(() => abrirRelatorio(r)).catch(e => Alert.alert(t('Não foi possível abrir'), String(e?.message ?? e)));
  return (
    <View style={{ backgroundColor: C.card, borderRadius: 16, padding: 12, gap: 8, borderWidth: 1, borderColor: alpha(cor, 0.3) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 16 }}>🗂️</Text>
        <Text style={{ color: C.text, fontWeight: '800', flex: 1 }}>{t('Relatórios gerados ({n})', { n: lista.length })}</Text>
        {lista.length > 3 && <Pressable onPress={() => setTodos(x => !x)} hitSlop={8}><Text style={{ color: cor, fontWeight: '700', fontSize: 12 }}>{todos ? t('Mostrar menos') : t('Ver todos')}</Text></Pressable>}
      </View>
      {sel.barra}
      <Ordenavel itens={mostrar} chave={r => r.id} desligado gap={8} onOrdem={() => {}} selecao={sel.prop} render={r => (
        <AcoesItem lembrar={{ aba, id: 'rel:' + r.id, titulo: r.nome }} nome={r.nome} onExcluir={() => excluir(r)} semConfirmar={!!r.doApp} onArquivar={() => org.arquivar(r.id)}
          fixar={{ on: org.fixado(r.id), alternar: () => org.alternarFixo(r.id) }}
          aviso={r.doApp ? undefined : t('Sai só da lista. O arquivo continua onde foi salvo.')}>
          <Pressable onPress={() => abrir(r)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card2, borderRadius: 12, padding: 10 }}>
            <Text style={{ fontSize: 20 }}>{icone(r.mime)}</Text>
            <View style={{ flex: 1 }}>
              <Text style={{ color: C.text, fontWeight: '600' }} numberOfLines={1}>{r.nome}</Text>
              <Text style={{ color: C.sub, fontSize: 11 }} numberOfLines={1}>{br(new Date(r.criado))} {horaTela(hm(new Date(r.criado)))} · {onde(r)}</Text>
            </View>
            <Text style={{ color: cor, fontWeight: '700', fontSize: 12, marginRight: 18 }}>{t('Abrir')}</Text>
          </Pressable>
        </AcoesItem>
      )} />
      <Arquivados itens={sep.arquivados} chave={r => r.id} cor={cor} render={r => <Text style={{ color: C.text }} numberOfLines={1}>{icone(r.mime)} {r.nome}</Text>}
        onRestaurar={r => org.restaurar(r.id)} onExcluir={confirmar} />
      <Text style={{ color: C.mute, fontSize: 11 }}>{t('Toque para abrir no app do celular. Arraste para a direita para tirar da lista.')}</Text>
    </View>
  );
}
